import { useQueryClient } from "@tanstack/react-query"
import { useSnackbar } from "notistack"
import { useCallback, useMemo, useRef, useState } from "react"
import { generatePath, useNavigate } from "react-router"
import { v4 as uuidv4 } from "uuid"

import {
  chatQueries,
  useCreateSession,
  useDeleteSession,
  useSessions,
} from "./hooks/useChatQueries"
import {
  applyStreamPartUpdate,
  finalizeLatestAssistantTurn,
  getStreamPartFields,
  hasResponseStatus,
  isAssistantStreamType,
  ReconnectNeededError,
  removePartsForSession,
  resolvePartId,
  StopRetryError,
} from "./hooks/chatStreamReducer"
import type { AppendChunkState, SSEData } from "./hooks/chatStreamReducer"
import type { SSEMessage } from "./useSSE"
import { useChatModelState } from "./hooks/useChatModelState"
import { computeMessages } from "./hooks/useComputedMessages"
import type {
  ChatMessage,
  ChatModelOption,
  ChatPart,
  ModelId,
  SessionInfo,
} from "./types"
import { withModel } from "./types"
import { AI_SERVICE_URL, ROUTES } from "../../utils/constants"
import { useSSE } from "./useSSE"
import { chatAxios } from "../../utils/aiService"

export interface NewChatMessage {
  title?: string
  query: string
}

export interface ChatSessionHook {
  messages: ChatMessage[]
  sendMessage: (message: NewChatMessage) => Promise<void>
  deleteSession: (id: string) => Promise<void>
  isStreaming: boolean
  streamingSessions: Record<string, boolean>
  stopStreaming: () => void
  sessionId: string | null
  activeSessionId: string | null
  setSessionId: (id: string | null) => void
  resetSession: () => void
  sessions: SessionInfo[]
  loadSession: (sessionId: string) => Promise<void>
  isLoadingHistory: boolean
  selectedModel: ModelId | null
  setModel: (model: ModelId) => void
  defaultModel: ModelId | null
  setDefaultModel: (model: ModelId) => void
  availableModels: ChatModelOption[]
  isLoadingModels: boolean
  hasModelsError: boolean
  retryModels: () => void
}

export const useChatSession = (): ChatSessionHook => {
  const navigate = useNavigate()
  const { postEventStream } = useSSE()
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  // We now store a flat list of ALL parts across all sessions we've loaded or streamed
  const [allParts, setAllParts] = useState<Record<string, ChatPart>>({})

  const [sessionId, setSessionId] = useState<string | null>(null)

  // Track streaming status per session
  const [streamingSessions, setStreamingSessions] = useState<
    Record<string, boolean>
  >({})
  const streamingSessionsRef = useRef<Record<string, boolean>>({})

  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false)

  const activeSessionRef = useRef<string | null>(null)
  const appendChunkStateRef = useRef<Record<string, AppendChunkState>>({})
  const activeAbortControllerRef = useRef<AbortController | null>(null)

  const { data: fetchedSessions = [], refetch: refetchSessions } = useSessions()
  const { mutateAsync: deleteSessionMutation } = useDeleteSession()
  const { mutateAsync: createSessionMutation, isPending: isCreatingSession } =
    useCreateSession()

  const {
    availableModels,
    isLoadingModels,
    hasModelsError,
    retryModels,
    selectedModel,
    setModel,
    defaultModel,
    setDefaultModel,
    resolveAvailableModelId,
    setSessionModelValue,
    resetModel,
    clearSessionModel,
  } = useChatModelState(sessionId, fetchedSessions, activeSessionRef)

  const setSessionStreaming = useCallback((id: string, value: boolean) => {
    streamingSessionsRef.current[id] = value
    setStreamingSessions(prev => ({ ...prev, [id]: value }))
  }, [])

  // Bind appendChunkStateRef into the pure reducer so call sites stay clean.
  const applyUpdate = useCallback(
    (
      prev: Record<string, ChatPart>,
      partId: string,
      incomingPart: ChatPart,
      streamData: SSEData
    ) =>
      applyStreamPartUpdate(
        appendChunkStateRef,
        prev,
        partId,
        incomingPart,
        streamData
      ),
    []
  )

  const resetSession = useCallback((): void => {
    activeSessionRef.current = null
    setSessionId(null)
    resetModel()
    appendChunkStateRef.current = {}
  }, [resetModel])

  const deleteSession = useCallback(
    async (id: string) => {
      try {
        await deleteSessionMutation(id)
        if (sessionId === id) resetSession()
        clearSessionModel(id)
        setAllParts(prev => removePartsForSession(prev, id))
      } catch (error) {
        console.error("Error deleting session:", error)
      }
    },
    [sessionId, deleteSessionMutation, resetSession, clearSessionModel]
  )

  const loadSession = useCallback(
    async (id: string) => {
      // Synchronous protection against race conditions destroying the UI
      if (activeSessionRef.current === id && streamingSessionsRef.current[id]) {
        return
      }

      appendChunkStateRef.current = {}

      setIsLoadingHistory(true)
      activeSessionRef.current = id
      setSessionId(id)

      try {
        // fetchQuery returns cached data when still fresh, deduplicates concurrent requests.
        const data = await queryClient.fetchQuery({
          queryKey: chatQueries.messages(id),
          queryFn: async () => {
            const response = await chatAxios.get<ChatPart[] | undefined>(
              `/api/ai/sessions/${id}/messages/`
            )
            return response.data ?? []
          },
        })

        let flatParts: Record<string, ChatPart> = {}
        for (const p of data) {
          const pId = resolvePartId({
            partId: p.part_id,
            messageId: p.message_id,
            index: p.index,
          })

          const streamData: SSEData = {
            type: p.type,
            chunk_mode: p.chunk_mode,
            seq: p.seq,
            event_id: p.event_id,
            final: p.final,
          }

          flatParts = applyUpdate(flatParts, pId, p, streamData)
        }

        // A loaded session is always complete — force-finalize any
        // append-mode parts whose `final` flag wasn't persisted by the
        // backend, so the spinner never shows for historical messages.
        for (const [key, part] of Object.entries(flatParts)) {
          if (part.chunk_mode === "append" && part.final !== true) {
            flatParts[key] = { ...part, final: true }
          }
        }

        setAllParts(prev => ({
          ...removePartsForSession(prev, id),
          ...flatParts,
        }))
      } catch (e) {
        console.error("Failed to load session history:", e)
      } finally {
        if (activeSessionRef.current === id) {
          setIsLoadingHistory(false)
        }
      }
    },
    [applyUpdate, queryClient]
  )

  const sendMessage = useCallback(
    async (message: NewChatMessage): Promise<void> => {
      const resolvedModel = resolveAvailableModelId(selectedModel)

      if (isLoadingModels) {
        enqueueSnackbar("Loading available models. Try again in a moment.", {
          variant: "info",
        })
        return
      }

      if (hasModelsError || resolvedModel == null) {
        enqueueSnackbar(
          "Chat models are unavailable. Reload once the backend is healthy.",
          { variant: "error" }
        )
        return
      }

      if (selectedModel !== resolvedModel) {
        setModel(resolvedModel)
      }

      const messageId = uuidv4()
      const requestAbortController = new AbortController()
      activeAbortControllerRef.current = requestAbortController
      let activeSessionId = sessionId
      const { title, query } = message

      if (!sessionId && !isCreatingSession) {
        try {
          const newSession = await createSessionMutation({
            title: title || query.substring(0, 50),
          })
          const newSessionId = newSession.id
          activeSessionId = newSessionId

          // Bind the current model to the new session id
          setSessionModelValue(newSessionId, resolvedModel)

          // Populate as null chat in frontend
          setAllParts(prev => removePartsForSession(prev, newSessionId))

          setSessionId(newSessionId)
          activeSessionRef.current = newSessionId
          setSessionStreaming(newSessionId, true)
          void navigate(
            generatePath(ROUTES.WINGMAN_CHAT, { chatId: newSessionId }),
            { replace: true }
          )
        } catch (e) {
          console.error(
            "Failed to create Django session, falling back to local uuid:",
            e
          )
          activeSessionId = uuidv4()
          setSessionModelValue(activeSessionId, resolvedModel)
          setSessionId(activeSessionId)
          activeSessionRef.current = activeSessionId
          setSessionStreaming(activeSessionId, true)
          void navigate(
            generatePath(ROUTES.WINGMAN_CHAT, { chatId: activeSessionId }),
            { replace: true }
          )
        }
      } else if (activeSessionId) {
        setSessionStreaming(activeSessionId, true)
      }

      if (!activeSessionId) {
        console.error("No active session id available for streaming")
        return
      }

      const resolvedSessionId = activeSessionId

      // 1. Immediately create a user part and add it to our state
      const userPartId = `${messageId}_0`
      const userPart: ChatPart = {
        type: "text",
        content: query,
        timestamp: new Date().toISOString(),
        latency_ms: 0,
        metadata: {},
        role: "user",
        session_id: resolvedSessionId,
        message_id: messageId,
        part_id: userPartId,
      }

      setAllParts(prev => ({ ...prev, [userPartId]: userPart }))

      // Whether the current connection received a terminal event (done/error).
      // Reset to false at the start of each reconnect attempt.
      let streamEndedCleanly = false

      // ── Shared SSE handlers ──────────────────────────────────────────────
      // Extracted so the same logic is used for the initial connection and
      // every reconnect attempt without duplication.

      const handleMessage = (ev: SSEMessage): void => {
        try {
          const data = JSON.parse(ev.data) as SSEData

          const targetSession = data.session_id || resolvedSessionId

          // Track whether this connection received a terminal event so that
          // handleClose can distinguish a clean close from an unexpected drop.
          if (data.type === "done" || data.type === "error") {
            streamEndedCleanly = true
          }

          if (data.type === "metadata") {
            // Server echoes back the model it resolved — adopt it.
            const confirmedModel = resolveAvailableModelId(data.model)
            if (confirmedModel) setModel(confirmedModel)
            return
          }

          if (data.type === "done") {
            setSessionStreaming(targetSession, false)
            setAllParts(prev =>
              finalizeLatestAssistantTurn(prev, targetSession)
            )
            void refetchSessions()
            return
          }

          if (data.type === "error") {
            console.error("Stream error:", data.content)
            setSessionStreaming(targetSession, false)
            setAllParts(prev =>
              finalizeLatestAssistantTurn(prev, targetSession)
            )
            return
          }

          if (isAssistantStreamType(data.type)) {
            const partId = resolvePartId({
              partId: data.part_id,
              messageId: data.message_id,
              index: data.index,
            })
            const newPart: ChatPart = {
              type: data.type,
              content: data.content ?? "",
              timestamp: data.timestamp || new Date().toISOString(),
              latency_ms: 0,
              metadata: {},
              role: "assistant",
              index: data.index,
              session_id: targetSession,
              part_id: partId,
              message_id: data.message_id,
              entity_id: data.entity_id,
              human_readable_name: data.human_readable_name,
              ...getStreamPartFields(data),
            }

            setAllParts(prev => applyUpdate(prev, partId, newPart, data))
          }
        } catch (err) {
          console.error("Failed to parse event:", err)
        }
      }

      const handleClose = (): void => {
        if (requestAbortController.signal.aborted) return
        if (!streamEndedCleanly) {
          // Stream dropped without a terminal event — signal reconnect.
          throw new ReconnectNeededError()
        }
        throw new StopRetryError("Connection closed normally")
      }

      const handleError = (err: unknown): void => {
        if (
          err instanceof StopRetryError ||
          err instanceof ReconnectNeededError
        ) {
          throw err
        }
        console.error("SSE stream onerror():", err)
        // Treat network-level errors as reconnectable rather than terminal.
        throw new ReconnectNeededError()
      }

      const connectToStream = async (msg: string): Promise<void> =>
        postEventStream({
          url: `${AI_SERVICE_URL}/stream/chats/${resolvedSessionId}/`,
          body: withModel(
            msg ? { message: msg, message_id: messageId } : { message: "" },
            resolvedModel
          ),
          signal: requestAbortController.signal,
          onmessage: handleMessage,
          onclose: handleClose,
          onerror: handleError,
        })

      // ── Initial connection ───────────────────────────────────────────────
      const MAX_RECONNECT_ATTEMPTS = 3
      let connectionError: unknown = null

      try {
        await connectToStream(query)
        return
      } catch (error: unknown) {
        if (error instanceof StopRetryError) return
        if (error instanceof DOMException && error.name === "AbortError") {
          console.log("Stream was aborted cleanly.")
          return
        }
        if (hasResponseStatus(error, 422)) {
          enqueueSnackbar(
            "Invalid model selection. Please choose a different model.",
            { variant: "error" }
          )
          setSessionStreaming(resolvedSessionId, false)
          return
        }
        if (!(error instanceof ReconnectNeededError)) {
          console.error(
            "🔴 [useChatSession] Outer catch block caught streaming error:",
            error
          )
          setSessionStreaming(resolvedSessionId, false)
          return
        }
        connectionError = error
      }

      // ── Reconnect loop ───────────────────────────────────────────────────
      // The server replays its rolling event buffer on re-registration, so
      // reconnecting clients receive missed events automatically. The reducer
      // is idempotent: replace-mode events dedup via event_id/seq, and
      // append-mode text chunks dedup via seq-keyed chunksBySeq maps.
      for (let attempt = 1; attempt <= MAX_RECONNECT_ATTEMPTS; attempt++) {
        console.warn(
          `[useChatSession] Stream dropped. Reconnecting (attempt ${attempt}/${MAX_RECONNECT_ATTEMPTS})…`
        )
        await new Promise<void>(resolve => setTimeout(resolve, attempt * 1000))

        streamEndedCleanly = false
        try {
          await connectToStream("")
          connectionError = null
          break
        } catch (reconnectErr: unknown) {
          if (
            reconnectErr instanceof DOMException &&
            reconnectErr.name === "AbortError"
          )
            return
          if (reconnectErr instanceof StopRetryError) return
          connectionError = reconnectErr
          // ReconnectNeededError or unknown: continue to next attempt
        }
      }

      if (connectionError != null) {
        console.error(
          "🔴 [useChatSession] Stream failed after all reconnect attempts:",
          connectionError
        )
        setSessionStreaming(resolvedSessionId, false)
      }
    },
    [
      sessionId,
      refetchSessions,
      createSessionMutation,
      enqueueSnackbar,
      hasModelsError,
      isCreatingSession,
      isLoadingModels,
      navigate,
      postEventStream,
      resolveAvailableModelId,
      selectedModel,
      applyUpdate,
      setSessionModelValue,
      setSessionStreaming,
      setModel,
    ]
  )

  const stopStreaming = useCallback(() => {
    const id = sessionId
    if (!id) return
    activeAbortControllerRef.current?.abort()
    activeAbortControllerRef.current = null
    setSessionStreaming(id, false)
    setAllParts(prev => finalizeLatestAssistantTurn(prev, id))
  }, [sessionId, setSessionStreaming])

  const messages = useMemo(
    () => computeMessages(allParts, sessionId),
    [allParts, sessionId]
  )

  return {
    messages,
    sendMessage,
    deleteSession,
    isStreaming: streamingSessions[sessionId || ""] || false,
    streamingSessions,
    stopStreaming,
    sessionId,
    activeSessionId: activeSessionRef.current,
    setSessionId,
    resetSession,
    sessions: fetchedSessions,
    loadSession,
    isLoadingHistory,
    selectedModel,
    setModel,
    defaultModel,
    setDefaultModel,
    availableModels,
    isLoadingModels,
    hasModelsError,
    retryModels,
  }
}
